<template>
  <div class="page-container tasks-page-section">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button v-show="activeTabIndex !== 2" class="custom-btn-secondary mr-1" :disabled="selected.length === 0 || isBusyExport" @click="exportTimesheetFile()">
          <b-spinner v-if="isBusyExport" small/>
          <v-icon name="buttons/table" :size="12"/>
          Скачать Табель
        </b-button>
        <b-button size="sm" class="custom-btn-secondary mr-1" v-if="visibleAdmins() && activeTabIndex !== 2" :disabled="isBusyExport" @click="showImportModal()">
          <b-spinner v-if="isBusyExport" small/>
          <v-icon name="buttons/export" :size="12"/>
          Импорт
        </b-button>
        <export-sidebar
          v-show="activeTabIndex !== 2"
          sidebar-id="sidebar-tasks-export"
          storage-key="sidebar_tasks_export"
          :columns="exportableFields"
          :data-provider="provideExportData"
          :data-mapper="mapTaskForExport"
          file-name="Tasks.xlsx"
          sheet-name="Tasks"
          :is-busy="isBusy"
          :items-count="totalRows"
        >
          <template #extra-buttons>
            <b-button variant="link" class="text-small text-dark" style="text-decoration: none !important" @click="toggleTW()">
              <v-icon name="buttons/profile" :size="13" class="mr-1"/>
              {{ isIncludeTW ? 'Скрыть детали' : 'Детали' }}
            </b-button>
          </template>
        </export-sidebar>
        <portal-target v-show="activeTabIndex === 2" name="task-timesheet-export" />
        <b-button v-show="activeTabIndex !== 2" id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-button v-if="canEditTasks()" class="custom-btn-primary" :disabled="isBusy" @click="showCreateModal()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новая группа заявок
        </b-button>
      </div>
    </header>

    <div class="page-body">
      <b-tabs class="custom-tabs" v-model="activeTabIndex">
        <template #tabs-end>
          <li v-show="activeTabIndex === 2" class="ml-auto d-flex align-items-center" role="presentation">
            <portal-target name="task-timesheet-actions" />
          </li>
        </template>
        <b-tab active>
          <template #title>
            <v-icon name="buttons/table" :size="13" class="mr-2"/>
            <span>Таблица</span>
          </template>

          <div class="page-filters my-2">
            <filters-drawer
              :query.sync="filters.query"
              :is-busy="isBusy"
              :filter-tags="activeFilterTags"
              @remove-tag="removeFilterTag"
              @submit="onSearch"
              @clear="onClearFilters"
            >
              <b-col cols="12">
                <label class="small text-muted">Период</label>
                <b-row class="align-items-center">
                  <b-col>
                    <b-form-input
                      id="input-start-date"
                      size="sm"
                      type="date"
                      v-model="filters.start_date"
                    />
                  </b-col>
                  <b-col cols="auto" class="px-2">
                    <span>&mdash;</span>
                  </b-col>
                  <b-col>
                    <b-form-input
                      id="input-end-date"
                      size="sm"
                      type="date"
                      v-model="filters.end_date"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col cols="12" class="mt-1">
                <label class="small text-muted">Объекты</label>
                <location-multi-select
                  id="location-multi-select"
                  ref="location_multi_select"
                  @onSelect="onSelectLocation"
                  :start-date="filters.start_date"
                  :end-date="filters.end_date"
                  :show-date-button="true"
                />
              </b-col>
              <b-col v-if="visibleAdmins()" cols="12" class="mt-1">
                <label class="small text-muted">Сотрудники</label>
                <user-multi-select
                  id="user-multi-select"
                  ref="user_multi_select"
                  @onSelect="onSelectUser"
                />
              </b-col>
              <b-col cols="12" class="mt-1">
                <label class="small text-muted">Вид работ</label>
                <work-type-multi-select
                  id="work-type-multi-select"
                  ref="work_type_multi_select"
                  v-model="filters.work_type_ids"
                  @onSelect="onSelectWorkType"
                />
              </b-col>
              <b-col cols="12" class="mt-1">
                <label class="small text-muted">Группа</label>
                <b-form-select
                  id="input-task-group"
                  size="sm"
                  v-model="filters.group_id"
                  :options="taskGroupOptions"
                />
              </b-col>
              <b-col md="6" cols="12" class="mt-1">
                <label class="small text-muted">Статус заявки</label>
                <b-form-select
                  id="input-statuses"
                  size="sm"
                  v-model="filters.status_id"
                >
                  <option :value="0">Все статусы</option>
                  <option v-for="option in activeStatuses" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-col>
              <b-col md="6" cols="12" class="mt-1">
                <label class="small text-muted">Статус оплаты</label>
                <b-form-select
                  id="input-paid-workers-statuses"
                  size="sm"
                  v-model="filters.paid_workers"
                >
                  <option :value="0">Все статусы оплаты</option>
                  <option v-for="option in activePaidWorkersStatuses" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </option>
                </b-form-select>
              </b-col>
            </filters-drawer>
          </div>

          <template v-if="isBusy || (items && items.length > 0)">
            <div class="page-table-container">
              <b-table
                id="tasks"
                ref="tasks"
                class="custom-table mb-0 text-small"
                select-mode="multi"
                :tbody-tr-class="tbodyRowClass"
                sticky-header
                responsive
                :busy="isBusy"
                :items="items"
                :fields="renderedFields"
                no-provider-filtering
                no-provider-paging
                no-provider-sorting
                no-local-sorting
                @sort-changed="sortChanged"
                @row-clicked="onRowClicked"
                @row-dblclicked="onRowDblClicked"
              >
                <template #table-busy>
                  <tables-skeleton :fields="renderedFields" :rows="6"/>
                </template>

                <template #head(selected)="row">
                  <b-form-checkbox
                    class="mx-1"
                    v-model="allSelected"
                    :indeterminate="indeterminate"
                    :disabled = "allSelectDisabled"
                    size="md"
                    @change="selectAllRows"
                  />
                </template>

                <template #cell(selected)="row">
                  <b-form-checkbox
                    class="mx-1"
                    size="md"
                    :checked="row.item.selected"
                    @click.native.stop
                    @change="onRowClicked(row.item)"
                  />
                </template>

                <template #cell(location_name)="row">
                  {{row.item.location_name}}
                  <p class="m-0 p-0 text-secondary text-small">{{row.item.customer_name}}</p>
                </template>

                <template #cell(start_date)="row">
                  {{formatTableDateTime(row.item.start_date)}}
                </template>

                <template #cell(tw_worker_phone)="row">
                  {{ phoneString(row.item.tw_worker_phone) }}
                </template>

                <template #cell(invoice_numb)="row">
                  <b-button v-if="visibleAdmins() && row.item.invoice_numb" variant="link" size="sm" style="color: #1A9167 !important;" @click.stop="showInvoice(row.item)">
                    {{row.item.invoice_numb}}
                  </b-button>
                  <div v-else>
                    {{row.item.invoice_numb}}
                  </div>
                </template>

                <template #cell(status_id)="row">
                  <div class="d-flex justify-content-between align-items-center">
                <span :class="['custom-tag', getStatusStyle(row.item.status_id)]">
                  {{getStatusName(row.item.status_id)}}
                </span>
                    <span>
                  <font-awesome-icon
                    :icon="['fas', paidWorkers(row).icon]"
                    :style=paidWorkers(row).style
                    v-b-tooltip.hover.top title="Оплата исполнителям"
                  />
                  <font-awesome-icon
                    :icon="['fas', readOnly(row).icon]"
                    :style=readOnly(row).style
                    v-b-tooltip.hover.top title="Редактирование заявки"
                  />
                </span>
                  </div>
                </template>

                <template #cell(executor_name)="row">
                  <div class="text-truncate" style="max-width: 180px;">
                    {{ row.item.executor_name }}
                  </div>
                  <p class="m-0 p-0 text-small">{{ row.item.total_manager }} ₽</p>
                </template>

                <template #cell(location_rate)="row">
                  {{row.item.payment_first}} ₽
                  <p class="m-0 p-0 text-small">{{ getUnit(row.item.unit_code) }}</p>
                </template>

                <template #cell(action)="row">
                  <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" no-caret class="custom-dropdown">
                    <template #button-content>
                      <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                    </template>
                    <b-dropdown-item variant="success" @click.stop="onRead(row.item)" target="_blank">
                      <v-icon name="buttons/eye" :size="15" class="mr-2"/>
                      Смотреть
                    </b-dropdown-item>
                    <b-dropdown-item variant="primary" v-if="isVisibleReadOnlyForRow(row.item)" @click="onEdit(row.item)">
                      <v-icon name="buttons/edit" :size="15" class="mr-2"/>
                      Редактировать
                    </b-dropdown-item>
                    <b-dropdown-item variant="info" @click="onCopy(row.item)">
                      <v-icon name="buttons/copy" :size="15" class="mr-2"/>
                      Копировать
                    </b-dropdown-item>
                    <b-dropdown-item variant="warning" v-if="row.item.invoice_numb" @click="onInvoiceFile(row.item)">
                      <v-icon name="navbar/invoices" :size="15" class="mr-2"/>
                      Скачать счёт
                    </b-dropdown-item>
                    <b-dropdown-item v-if="isVisibleReadOnlyForRow(row.item)" variant="danger" class="btn-delete" @click="onRemove(row)">
                      <v-icon name="buttons/trash" :size="15" class="mr-2"/>
                      Удалить
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </div>

            <div class="page-pagination">
              <pagination-footer
                use-router
                count-right
                :current-page.sync="currentPage"
                :total-rows="totalRows"
                :per-page.sync="perPage"
                :page-options="pageOptions"
                @page-click="onPagination"
                @per-page-change="onPerPageChange"
              >
                <template #footer-update>
                  <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
                <span class="text-small">
                  С заказчика: <b>{{totalSelectedPrice}}</b>
                  | Кол-во исп: <b>{{totalSelectedWorkers}}</b>
                  | Кол-во: <b>{{totalSelectedUnits}}</b>
                  | Исполнителям: <b>{{totalSelectedWorkerPrice}}</b>
                  | Менеджеру: <b>{{totalSelectedManager}}</b>
                  | Руководителю: <b>{{totalSelectedProject}}</b>
                </span>
                    <div v-if="selected.length > 0" class="d-flex flex-wrap align-items-center" style="gap: 8px;">
                      <b-button v-if="visibleAdmins()" class="custom-btn-tag text-small" :style="{'--btn-color': '#F0F5FF', '--btn-border': '#D9E0FF', '--btn-text': '#061178'}"  :disabled="!allStatusesEqual || newStatusDisabled" @click="onNewStatus()">
                        <v-icon name="buttons/undo" class="mr-1" :size="10"/>
                        На доработку
                      </b-button>
                      <b-button class="custom-btn-tag text-small" :style="{'--btn-color': '#85A5FF', '--btn-border': '#6C8EEA', '--btn-text': '#1740B4'}" :disabled="!allStatusesEqual || payStatusDisabled" @click="onPayStatus()">
                        <v-icon name="buttons/receipt" class="mr-1" :size="10"/>
                        На оплату
                      </b-button>
                      <b-button v-if="visibleAdmins()" class="custom-btn-tag text-small" :style="{'--btn-color': '#FFF7E6', '--btn-border': '#FFE58F', '--btn-text': '#FAAD14'}" :disabled="payWorkersDisabled" @click="onPayWorkers()">
                        <v-icon name="buttons/payment" class="mr-1" :size="10"/>
                        Оплатить исполнителям
                      </b-button>
                      <b-button v-if="visibleAdmins()" class="custom-btn-tag text-small" :style="{'--btn-color': '#85A5FF', '--btn-border': '#6C8EEA', '--btn-text': '#262626'}" :disabled="!allStatusesEqual || invoiceDisabled" @click="onInvoice()">
                        <v-icon name="navbar/stamp" class="mr-1" :size="10"/>
                        Выставить счёт
                      </b-button>
                    </div>
                  </div>
                </template>
                <template #count-right>
              <span v-if="selected.length > 0" class="text-small mr-2">
                Выбрано: <b>{{ selected.length }}</b>
              </span>
                  <span v-if="selected.length > 0" class="vertical-divider"/>
                  <span class="text-small text-muted">Записей: <b>{{ totalRows }}</b></span>
                  <span class="vertical-divider"/>
                </template>
              </pagination-footer>
            </div>
          </template>

          <div v-else class="empty-state-container">
            <v-icon name="navbar/empty_table" :size="90"/>
            <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных заявок.' }}</h5>
            <div v-if="!isFiltered">
              <p>Создайте новую заявку прямо сейчас</p>
              <b-btn v-if="canEditTasks()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
                <v-icon name="buttons/plus" :size="12" class="mr-1"/>
                Новая группа заявок
              </b-btn>
            </div>
          </div>
        </b-tab>
        <b-tab>
          <template #title>
            <v-icon name="buttons/calendar" :size="13" class="mr-2"/>
            <span>Календарь</span>
          </template>

          <div class="page-body overflow-auto">
            <div class="d-flex">
              <select
                v-model="currentMonth"
                @change="onCalendarPeriodChange"
                class="custom-select-filters mr-2"
              >
                <option v-for="(m, index) in monthNames" :key="index" :value="index">{{ m }}</option>
              </select>
              <select
                v-model="currentYear"
                @change="onCalendarPeriodChange"
                class="custom-select-filters"
              >
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>

            <div>
              <div class="calendar-grid header">
                <div v-for="day in weekDays" :key="day" class="weekday-cell">{{ day }}</div>
              </div>

              <div class="calendar-grid body">
                <div
                  v-for="day in calendarDays"
                  :key="day.dateString"
                  class="date-cell"
                  :class="{ 'is-not-current-month': !day.isCurrentMonth }"
                >
                  <div class="d-flex justify-content-between align-items-center w-100">
                    <span class="day-number">{{ day.dayNumber }}</span>
                  </div>

                  <div class="tasks-tags-wrapper w-100 mt-1">
                    <div
                      v-for="task in day.visibleTasks"
                      :key="task.id"
                      class="calendar-task-tag text-truncate"
                      @click.stop="handleCalendarTaskClick(task)"
                      :title="task.location_name"
                    >
                      #{{ task.id }} {{ task.location_name }}
                    </div>
                  </div>

                  <div class="mt-auto pt-1 w-100" v-if="day.visibleTasks && day.visibleTasks.length > 0">
                    <div
                      v-if="day.hiddenTasksCount > 0"
                      class="calendar-task-tag more-tag text-center"
                      @click.stop="showTasksInTable(day.dateString)"
                    >
                      Ещё +{{ day.hiddenTasksCount }} ...
                    </div>
                    <div
                      v-else
                      class="calendar-task-tag more-tag text-center"
                      @click.stop="showTasksInTable(day.dateString)"
                    >
                      Перейти
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>
        <b-tab lazy>
          <template #title>
            <v-icon name="buttons/table_block" :size="13" class="mr-2"/>
            <span>Табель</span>
          </template>
          <task-final-timesheet :group-options="taskGroupOptions" />
        </b-tab>
      </b-tabs>
    </div>

    <tasks-import ref="tasks_import" @refresh="handleTasksImport"/>

    <dynamic-tables
      storage-key="tasks_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />

    <task-group-create ref="task_group_create_modal" @refresh="getItems"/>

    <b-modal
      id="modal-select-pay-organization"
      ref="modalSelectPayOrganization"
      title="Организация"
      @ok="onPay"
      ok-title="Оплатить"
      button-size="sm"
      :ok-disabled="currentPayOrganization == null || isPaying"
      :cancel-disabled="isPaying"
      :hide-header-close="isPaying"
      :no-close-on-backdrop="isPaying"
      :no-close-on-esc="isPaying"
      :cancel-title="$t('message.actions.cancel')"
    >
      <organization-select
        id="input-org-select"
        ref="organization_select"
        :value="currentPayOrganization"
        :is-disabled="isPaying"
        @onSelect="onSelectOrg"
      />
      <b-alert v-if="paymentWarning" show variant="warning" class="mt-3 mb-0">
        {{ paymentWarning }}
      </b-alert>
    </b-modal>
    <b-modal ref="modalPayResult" title="Баланс Про" ok-only ok-title="Понятно" button-size="sm">
      <b-alert v-for="(message, index) in paymentMessages" :key="'message-' + index" show variant="info">
        {{ message }}
      </b-alert>
      <b-alert v-for="(error, index) in paymentErrors" :key="'error-' + index" show variant="danger">
        {{ error }}
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import tasks from "../../network/tasks";
import locations from "../../network/locations";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
import invoices from "../../network/invoices";
import balansPro from "../../network/balansPro";
  import dateFormatter from '../../utilites/dateFormatter';
  import helpers from "../../utilites/helpers";
  import permissions, {PERM} from "../../utilites/permissions";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import unitsStore from "../../utilites/unitStore";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
  import {parse} from "content-disposition-attachment";
    import TasksImport from "./TasksImport";
    import LocationMultiSelect from "../Locations/LocationMultiSelect";
    import UserMultiSelect from "../Users/UserMultiSelect";
    import OrganizationSelect from "../Organizations/OrganizationSelect";
    import TaskGroupCreate from "./TaskGroupCreate.vue";
    import TaskFinalTimesheet from "./TaskFinalTimesheet.vue";
    import WorkTypeMultiSelect from "../Catalogs/WorkType/WorkTypeMultiSelect.vue";

export default {
  name: 'Tasks',
  components: { WorkTypeMultiSelect, TaskFinalTimesheet, TaskGroupCreate, TablesSkeleton, PaginationFooter, ExportSidebar, FiltersDrawer, DynamicTables, TasksImport, LocationMultiSelect, UserMultiSelect, OrganizationSelect },
  mixins: [
    tasks, locations, catalogs, users, invoices, balansPro,
    dateFormatter, helpers, unitsStore, permissions, pageSearchService
  ],
  data() {
    return {
      activeTabIndex: Number(sessionStorage.getItem('tasks_active_tab')) || 0,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      weekDays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
      monthNames: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
      title: 'Заявки на смену',
      isBusy: false,
      isBusyExport: false,
      items: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class: 'text-center',
        thStyle: { width: '50px', 'min-width': '50px' },
      },
      fields: [
        {
          key: 'id',
          label: 'ID',
          class: 'text-center',
          thStyle: { width: '60px', 'min-width': '60px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'group_name',
          label: 'Группа',
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'location_name',
          label: this.$t('message.tasks.table.location_name'),
          thStyle: { width: '200px', 'min-width': '200px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'start_date',
          label: 'Дата',
          thStyle: { width: '130px', 'min-width': '130px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'workers_count',
          label: this.$t('message.tasks.table.workers_count'),
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'total_hours',
          label: 'Кол-во ед.',
          thStyle: { width: '90px', 'min-width': '90px' },
          sortable: true,
          visible: true
        },
        {
          key: 'total_worker_price_with_comiss',
          label: 'Исполнителям, ₽',
          thStyle: { width: '130px', 'min-width': '130px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'total_price',
          label: 'C заказчика, ₽',
          thStyle: { width: '120px', 'min-width': '120px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'invoice_numb',
          label: 'Счёт',
          thStyle: { width: '60px', 'min-width': '60px' },
          sortable: true,
          visible: true
        },
        {
          key: 'status_id',
          label: 'Статус',
          thStyle: { width: '150px', 'min-width': '150px' },
          sortable: true,
          visible: true
        },
        {
          key: 'executor_name',
          label: 'Менеджер',
          thStyle: { width: '200px', 'min-width': '200px', 'max-width': '200px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'location_rate',
          label: 'Ставка объекта',
          thStyle: { width: '80px', 'min-width': '80px' },
          sortable: true,
          visible: true
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.tasks.table.actions'),
        class: 'text-center',
        thStyle: { width: '80px', 'min-width': '80px' },
      },
      additionalExportFields: [
        {
          key: 'customer_name',
          label: "Заказчик",
          visible: true,
          exportable: true,
        },
        {
          key: 'start_address',
          label: "Адрес заявки",
          visible: true,
          exportable: true,
        },
        {
          key: 'end_date',
          label: 'Дата окончания',
          visible: true,
          exportable: true,
        },
        {
          key: 'payment_first',
          label: 'Ставка вида работ',
          visible: true,
          exportable: true,
        },
        {
          key: 'author_name',
          label: 'Автор',
          visible: true,
          exportable: true,
        },
        {
          key: 'create_date',
          label: 'Дата создания',
          visible: true,
          exportable: true,
        },
        {
          key: 'additional_cost',
          label: 'Добавленные расходы',
          visible: true,
          exportable: true,
        },
        {
          key: 'declarated_cost',
          label: 'Заявленные расходы',
          visible: true,
          exportable: true,
        },
        {
          key: 'cargo_description',
          label: 'Примечание',
          visible: true,
          exportable: true,
        },
        {
          key: 'status_name',
          label: 'Статус',
          visible: true,
          exportable: true,
        },
        {
          key: 'tw_worker_name',
          label: 'Исп. Имя',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_worker_phone',
          label: 'Исп. Телефон',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_worker_inn',
          label: 'Исп. ИНН',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_worker_card',
          label: 'Исп. Карта',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_price',
          label: 'Исп. Ставка',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_start_date',
          label: 'Исп. Дата начала',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_end_date',
          label: 'Исп. Дата окончания',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_dinner',
          label: 'Исп. Обед',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_total_units',
          label: 'Исп. Количество',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_prepaid',
          label: 'Исп. Аванс',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_road_price',
          label: 'Исп. Трансфер',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_total_price',
          label: 'Исп. Итого',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_description',
          label: 'Исп. Примечание',
          visible: false,
          exportable: true,
          isTW: true,
        },
      ],
      isIncludeTW: false,
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      perPageOptions: [10, 50, 100, 300, 500],
      deleteRow: {
        id: null,
        start_address: ''
      },
      executors: [],
      selected: [],
      allSelected: false,
      indeterminate: false,
      allSelectDisabled: true,
      statuses: [],
      taskGroupOptions: [
        { value: 0, text: 'Все группы' },
      ],
      paidStatuses: [
        {id: 1, name: 'Не оплачено'},
        {id: 2, name: 'Оплачено'},
      ],
      currentPayOrganization: null,
      currentPayTaskData: null,
      isPaying: false,
      isUpdatingStatus: false,
      paymentWarning: '',
      paymentMessages: [],
      paymentErrors: [],
      initialFilters: null,
    }
  },
  async created() {
    const savedTab = sessionStorage.getItem('tasks_active_tab');
    if (savedTab !== null) {
      this.activeTabIndex = Number(savedTab);
    }

    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.start_date) {
        this.filters.start_date = query.start_date;
      } else {
        this.filters.start_date = this.getStartDateFilter();
      }

      if (query.end_date) {
        this.filters.end_date = query.end_date;
      }

      if (query.status_id) {
        this.filters.status_id = query.status_id;
      }

      if (query.paid_workers) {
        this.filters.paid_workers = query.paid_workers;
      }

      if (query.work_type_ids) {
        const decodeWorkTypes = decodeURIComponent(query.work_type_ids);
        this.filters.work_type_ids = decodeWorkTypes.split(",").map(Number);
      }

      if (query.group_id) {
        this.filters.group_id = Number(query.group_id) || 0;
      }

      if (query.locations) {
        const decodeLoc = decodeURIComponent(query.locations);
        this.filters.locations = decodeLoc.split(",");
      }

      if (query.users) {
        const decodeLoc = decodeURIComponent(query.users);
        this.filters.users = decodeLoc.split(",");
      }
    } else {
      this.filters.start_date = this.getStartDateFilter();
      this.filters.end_date = null;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.initialFilters = JSON.stringify(this.filters);

    await this.getExecutors();

    this.statuses = (await this.getTaskStatuses()).data.items;
    this.taskGroupOptions = await this.getTaskGroupOptions();

    await this.onSearch();
  },
  async mounted() {
    if (this.$refs.location_multi_select) {
      await this.$refs.location_multi_select.configure(this.filters.locations);
    }

    if (this.$refs.user_multi_select) {
      await this.$refs.user_multi_select.configure(this.filters.users);
    }
  },
  computed: {
    allStatusesEqual() {
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v === arr[0]);
      return allEqual(taskStatuses);
    },
    newStatusDisabled() {
      if (this.isBusy || this.isUpdatingStatus) return true;
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v !== 2);

      const taskWorkerPaid = this.selected.map(obj => obj.paid_workers);
      const paid = taskWorkerPaid.find(x => x === true);

      return allEqual(taskStatuses) || paid != null;
    },
    payStatusDisabled() {
      if (this.isBusy || this.isUpdatingStatus) return true;
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v !== 1);
      return allEqual(taskStatuses);
    },
    payWorkersDisabled() {
      if (this.isBusy || this.isUpdatingStatus) return true;
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const status = taskStatuses.find(x => x === 1);

      const taskWorkerPaid = this.selected.map(obj => obj.paid_workers);
      const paid = taskWorkerPaid.find(x => x === true);

      return paid != null || status != null;
    },
    invoiceDisabled() {
      if (this.isBusy || this.isUpdatingStatus) return true;
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v !== 2);
      return allEqual(taskStatuses);
    },
    activeStatuses() {
      return this.statuses;
    },
    activePaidWorkersStatuses() {
      return this.paidStatuses;
    },
    totalSelectedPrice() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_price)
        .reduce((acc, current) => acc + current, 0),true
      );
    },
    totalSelectedWorkerPrice() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_worker_price)
        .reduce((acc, current) => acc + current, 0),true
      );
    },
    totalSelectedUnits() {
      return this.formatStatsUnitTotals(this.selected);
    },
    totalSelectedManager() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_manager)
        .reduce((acc, current) => acc + current, 0), true
      );
    },
    totalSelectedProject() {
      return this.priceFormat(
        this.selected
          .map(obj => obj.total_project ? obj.total_project : 0)
          .reduce((acc, current) => acc + current, 0), true
      );
    },
    totalSelectedWorkers() {
      return this.selected
        .map(obj => obj.workers_count)
        .reduce((acc, current) => acc + current, 0)
    },
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        { ...this.serviceStartField, sticky: 'left' },
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];
      return this.handleFixedCols(allFields);
    },
    exportableFields() {
      let allFields = [...this.fields, ...this.additionalExportFields]
        .filter(field => field.exportable);

      if (!this.isIncludeTW) {
        allFields = allFields.filter(field => !field.isTW);
      }

      return allFields.map(field => ({
        key: field.key,
        label: field.label,
        visible: field.visible,
      }));
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();
      const start = this.filters.start_date;
      const end = this.filters.end_date;

      if (start || end) {
        let dateTag = '';

        if (start && end) {
          dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
        } else if (start) {
          dateTag = `с ${this.formatTableDate(start)}`;
        } else {
          dateTag = `по ${this.formatTableDate(end)}`;
        }
        tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.users.length > 0) {
        tags.push({ key: 'user', label: `Сотрудники: ${this.filters.users.length}`,});
      }

      if (this.filters.status_id !== defaults.status_id) {
        const foundItem = this.activeStatuses.find(s => s.id === this.filters.status_id);
        tags.push({ key: 'status', label: foundItem ? foundItem.name : 'Статус заявки' });
      }

      if (this.filters.paid_workers !== defaults.paid_workers) {
        const foundItem = this.paidStatuses.find(s => s.id === this.filters.paid_workers);
        tags.push({ key: 'paid', label: foundItem ? foundItem.name : 'Статус оплаты' });
      }

      if (this.filters.work_type_ids.length > 0) {
        tags.push({ key: 'work_type', label: `Виды работ: ${this.filters.work_type_ids.length}` });
      }

      if (this.filters.group_id !== defaults.group_id) {
        const foundItem = this.taskGroupOptions.find(option => option.value === this.filters.group_id);
        tags.push({ key: 'group', label: foundItem ? foundItem.text : 'Группа' });
      }

      return tags;
    },
    isFiltered() {
      if (!this.initialFilters) return false;
      return JSON.stringify(this.filters) !== this.initialFilters;
    },
    /* calendar */
    availableYears() {
      const years = [];
      const current = new Date().getFullYear();
      for (let i = current - 3; i <= current + 1; i++) {
        years.push(i);
      }
      return years;
    },
    tasksByDateMap() {
      const map = {};
      if (!this.items || !this.items.length) return map;

      this.items.forEach(task => {
        if (!task.start_date) return;

        const localDate = this.getDateFromServerString(task.start_date);
        const dateKey = this.formatDate(localDate);

        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(task);
      });

      return map;
    },
    calendarDays() {
      const year = this.currentYear;
      const month = this.currentMonth;
      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);

      let firstDayIndex = firstDayOfMonth.getDay() - 1;
      if (firstDayIndex === -1) firstDayIndex = 6;

      const days = [];
      const prevMonthLastDay = new Date(year, month, 0).getDate();

      for (let i = firstDayIndex - 1; i >= 0; i--) {
        days.push(this.formatDayObject(new Date(year, month - 1, prevMonthLastDay - i), false));
      }
      for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
        days.push(this.formatDayObject(new Date(year, month, i), true));
      }

      const totalCells = days.length > 35 ? 42 : 35;
      const remaining = totalCells - days.length;
      for (let i = 1; i <= remaining; i++) {
        days.push(this.formatDayObject(new Date(year, month + 1, i), false));
      }

      return days;
    },
    pageOptions() {
      if (!this.filters.start_date) {
        return this.perPageOptions.filter(size => size <= 100);
      }

      return this.perPageOptions;
    }
  },
  watch: {
    selected(newValue, oldValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.totalRows) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
    totalRows(newValue, oldValue) {
      if (newValue === 0) {
        this.allSelectDisabled = true;
      } else if (newValue > 10000) {
        this.allSelectDisabled = true;
      } else {
        this.allSelectDisabled = false;
      }
    },
    async activeTabIndex(newVal) {
      sessionStorage.setItem('tasks_active_tab', newVal);

      if (newVal === 1) {
        this.onCalendarPeriodChange();
      }
      if (newVal === 2) {
        this.taskGroupOptions = await this.getTaskGroupOptions();
      }
    }
  },
  methods: {
    async getTaskGroupOptions() {
      const allOption = { value: 0, text: 'Все группы' };
      const result = await this.getTaskGroups({
        page: 1,
        size: 1000,
        sortBy: ['name'],
        sortDesc: [false],
      });

      if (!result || !result.data || !Array.isArray(result.data.items)) {
        return [allOption];
      }

      return [
        allOption,
        ...result.data.items.map(item => ({ value: item.id, text: item.name })),
      ];
    },
    async getExecutors() {
      const result = await this.getUsers();

      if (!result || !result.data) {
        this.executors = [];
        return;
      }

      this.executors = result.data.users;
    },
    visibleAdmins() {
      return this.hasPermission([PERM.TASK_EDIT_OTHER]);
    },
    async getItems() {
      const locationIds = this.filters.locations.map(id => `&locations[]=${id}`);
      const usersIds = this.filters.users.map(id => `&executors[]=${id}`);
      const workTypeIds = this.filters.work_type_ids.map(id => `&work_type_ids[]=${id}`);

      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status_id: this.filters.status_id,
        work_type_ids: workTypeIds.join(''),
        group_id: this.filters.group_id,
        paid_workers: this.filters.paid_workers,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
        include_tw: false,
      }

      this.isBusy = true;
      const result = await this.getTasks(data);

      if (!result || !result.data) {
        this.items = [];
        this.totalRows = 0;
        this.isBusy = false;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.tasks;
      this.totalRows = result.data.total;
      this.updateSelected();
      this.isBusy = false;
    },
    showCreateModal() {
      this.$refs.task_group_create_modal.showModal();
    },
    onCreate() {
      this.$router.push({ name: 'TasksAdd'}).catch(()=>{});
    },
    onEdit(item) {
      this.$router.push({ name: 'TasksEdit', params: { task_id: item.id }}).catch(()=>{});
    },
    onRead(item) {
      this.$router.push({ name: 'TasksView', params: { task_id: item.id }}).catch(()=>{});
    },
    onCopy(item) {
      this.$router.push({ name: 'TasksCopy', params: { task_id: item.id }}).catch(()=>{});
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      const title = "Удаление заявки на смену";
      const message = `${this.$t("message.tasks.modal.removeDesc")} № ${this.deleteRow.id}?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteTask(this.deleteRow);

        if (result.error) {
          if (result.status === 422) {
            const message = this.$t('message.common.errorMessage');
            this.showError(message);
          }
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заявка удалена.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    len(arr) {
      return arr ? arr.length : 0;
    },
    paidWorkers(row) {
      return this.yesOrNoPayIcon(row.item.paid_workers);
    },
    paid(row) {
      return this.yesOrNoText(row.item.paid);
    },
    // Filters
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.start_date = null;
        this.filters.end_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'user') {
        this.filters.users = [];
        this.$refs.user_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status_id = 0;
      }
      else if (tag.key === 'paid') {
        this.filters.paid_workers = 0;
      }
      else if (tag.key === 'work_type') {
        this.filters.work_type_ids = [];
        if (this.$refs.work_type_multi_select) this.$refs.work_type_multi_select.onClear();
      }
      else if (tag.key === 'group') {
        this.filters.group_id = 0;
      }
      this.onSearch();
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }

      this.filters = filters;

      if (this.$refs.location_multi_select) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.user_multi_select) {
        await this.$refs.user_multi_select.configure(this.filters.users);
      }

      if (this.$refs.work_type_multi_select) {
        this.filters.work_type_ids = [];
      }

      await this.onSearch();
      this.updateSelected();
    },
    getDefaultFilters() {
      let filters = {
        query: '',
        start_date: null,
        end_date: null,
        status_id: 0,
        users: [],
        locations: [],
        work_type_ids: [],
        group_id: 0,
        paid_workers: 0
      }

      filters.start_date = this.getStartDateFilter();
      filters.end_date = null;
      return filters;
    },
    getStartDateFilter() {
      // TODO: Продумать как менять даты для роли бухгалтера через permissions
      if (this.hasPermission([PERM.STAFF])) {
        return this.getStartDateFilterAccountant();
      }

      let date = this.getNewDateToServer();
      date.setDate(date.getDate() - 60);
      return this.formatDate(date);
    },
    getStartDateFilterAccountant() {
      let date = this.getNewDateToServer();
      date.setDate(date.getDate() - 30);
      return this.formatDate(date);
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(function (item) { return item.id; });
    },
    onSelectUser(users) {
      this.filters.users = users.map(function (item) { return item.id; });
    },
    onSelectWorkType(workTypes) {
      this.filters.work_type_ids = workTypes.map(item => item.id);
    },
    // permissions
    isVisibleReadOnlyForRow(item) {
      if (this.hasPermission([PERM.TASK_EDIT_OTHER])) {
        return true;
      }

      if (this.hasPermission([PERM.TASK_EDIT])) {
        if (item.read_only) {
          return false;
        }
        return true;
      }

      return false;
    },
    canEditTasks() {
      return this.hasPermission([PERM.TASK_EDIT]);
    },
    // Export
    toggleTW() {
      this.isIncludeTW = !this.isIncludeTW;
    },
    async provideExportData() {
      const items = await this.getAllItems(this.isIncludeTW);

      if (!items || items.length === 0) {
        this.showSubmitError("Записей не найдено.");
        return [];
      }

      if (this.totalRows > 1000) {
        this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
        return [];
      }

      if (!this.isIncludeTW) {
        return items;
      }

      const allItems = [];
      items.forEach(task => {
        if (task.task_workers && task.task_workers.length > 0) {
          task.task_workers.forEach(tw => {
            allItems.push({ ...task, tw: tw });
          });
        } else {
          allItems.push(task);
        }
      });

      return allItems;
    },
    mapTaskForExport(item) {
      const tw = item.tw || null;

      return {
        id: item.id,
        customer_name: item.customer_name,
        start_address: item.start_address,
        location_name: item.location_name,
        group_name: item.group_name,
        start_date: this.formatTableDate(item.start_date),
        end_date: this.formatTableDate(item.end_date),
        total_worker_price_with_comiss: item.total_worker_price_with_comiss,
        total_price: item.total_price,
        payment_first: item.payment_first,
        executor_name: item.executor_name,
        author_name: item.author_name,
        create_date: this.formatTableDate(item.create_date),
        workers_count: item.workers_count,
        additional_cost: item.additional_cost,
        declarated_cost: item.declarated_cost,
        cargo_description: item.cargo_description,
        status_name: item.status_name,

        tw_worker_name: tw ? tw.worker_name : '',
        tw_worker_phone: tw ? tw.worker_phone : '',
        tw_worker_inn: tw && tw.worker ? tw.worker.inn : '',
        tw_worker_card: tw && tw.worker && tw.worker.payments_card && tw.worker.payments_card.card_number
          ? tw.worker.payments_card.card_number
          : '',
        tw_price: tw ? tw.price : '',
        tw_start_date: tw ? (tw.start_date ? this.formatTableDateTime(tw.start_date) : '') : '',
        tw_end_date: tw ? (tw.end_date ? this.formatTableDateTime(tw.end_date) : '') : '',
        tw_dinner: tw ? tw.dinner : '',
        tw_total_units: tw ? tw.total_units : '',
        tw_prepaid: tw ? tw.prepaid : '',
        tw_road_price: tw ? tw.road_price : '',
        tw_total_price: tw ? tw.total_price : '',
        tw_description: tw ? tw.description : '',
      };
    },
    async getAllItems(includeTW) {
      const locationIds = this.filters.locations.map(id => `&locations[]=${id}`);
      const usersIds = this.filters.users.map(id => `&executors[]=${id}`);
      const workTypeIds = this.filters.work_type_ids.map(id => `&work_type_ids[]=${id}`);

      const data = {
        page: 1,
        size: this.totalRows,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status_id: this.filters.status_id,
        work_type_ids: workTypeIds.join(''),
        group_id: this.filters.group_id,
        paid_workers: this.filters.paid_workers,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
        include_tw: includeTW,
      }

      this.isBusyExport = true;
      const result = await this.getTasks(data);
      this.isBusyExport = false;

      if (result.data.tasks && result.data.tasks.length > 0) {
        return result.data.tasks;
      }

      return [];
    },
    showImportModal() {
      this.$refs.tasks_import.showModal();
    },
    handleTasksImport() {
      this.$bvToast.toast('Импорт заявок завершен.', {
        title: 'Импорт заявок',
        variant: 'success',
        autoHideDelay: 3000,
        solid: true
      });

      this.getItems();
    },
    onRowDblClicked(item) {
      if (this.isVisibleReadOnlyForRow(item)) {
        this.onEdit(item);
      } else {
        this.onRead(item);
      }
    },
    async selectAllRows(checked) {
      if (checked) {
        if (this.totalRows > 10000) {
          this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
          this.allSelected = false;
          return;
        }

        this.selected = await this.getAllItems(false);
        this.updateSelected();
      } else {
        this.selected = [];
        this.updateSelected();
      }
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    updateSelected() {
      if (!this.items) return;

      const currentItems = new Map(this.items.map(item => [item.id, item]));
      this.selected = this.selected.map(item => currentItems.get(item.id) || item);
      const selectedIds = new Set(this.selected.map(item => item.id));
      this.items.forEach((item) => {
        this.$set(item, 'selected', selectedIds.has(item.id));
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Статус
    getStatusName(status_id) {
      const status = this.statuses.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 1:
          return 'tag-info';
        case 2:
          return 'tag-primary';
        case 3:
          return 'tag-secondary';
        case 4:
          return 'tag-success';
        default:
          return '';
      }
    },
    onInvoice() {
      this.$router.push({ name: 'InvoiceAdd', params: { tasks: this.selected }}).catch(()=>{});
    },
    async onNewStatus() {
      await this.updateSelectedTasksStatus(1);
    },
    async onPayStatus() {
      if (this.isUpdatingStatus) return;
      const message = "Изменение приведет к закрытию заявки для редактирования.\nВы уверены что хотите продолжить?";
      const result = await this.showAcceptChangeStatus(message);
      if (!result) { return; }

      await this.updateSelectedTasksStatus(2);
    },
    async updateSelectedTasksStatus(statusId) {
      if (this.isUpdatingStatus || !this.selected.length) return;
      const taskIds = this.selected.map(item => item.id);
      this.isUpdatingStatus = true;
      try {
        const result = await this.postTaskUpdateStatus({task_ids: taskIds, status_id: statusId});
        if (!result || result.error || !result.data) {
          this.showError(result && result.message || 'Не удалось изменить статус заявок.');
          return;
        }

        // Selected tasks can be on other pages or leave the active status filter.
        const updatedIds = new Set(taskIds);
        this.selected.forEach(item => {
          if (!updatedIds.has(item.id)) return;
          this.$set(item, 'status_id', statusId);
          this.$set(item, 'read_only', statusId === 2);
        });
        await this.getItems();
      } finally {
        this.isUpdatingStatus = false;
      }
    },
    async onPayWorkers() {
      const message = "Изменение приведет к оплате всем исполнителям в заявке.\nВы уверены что хотите продолжить?";
      const result = await this.showAcceptChangeStatus(message);

      if (!result) { return; }

      const taskIds = this.selected.map(obj => obj.id);
      this.currentPayTaskData = {
        task_ids: taskIds,
      };
      this.currentPayOrganization = null;
      this.paymentWarning = '';
      this.$refs.modalSelectPayOrganization.show();
    },
    async showAcceptChangeStatus(message) {
      const title = "Изменение статуса";
      return await this.showAcceptModal(title, message);
    },
    showInvoice(item) {
      const routeData = this.$router.resolve({name: 'InvoiceEdit', params: { invoice_id: item.invoice_id }});
      window.open(routeData.href, '_blank');
    },
    onInvoiceFile(item) {
      const id = item.invoice_id;
      this.exportInvoiceFile(id, "pdf", 1);
    },
    async exportInvoiceFile(id, exportType, hasStamp) {
      const data = { id, exportType, hasStamp }

      const result = await this.getInvoiceFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание файла. Попробуйте повторить позднее.");
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },
    getFileName(disposition) {
      const defaultName = "untitled";

      if (!disposition) return defaultName;

      try {
        const { attachment, filename } = parse(disposition);
        return attachment ? filename : defaultName;
      } catch (e) {
        console.error(e);
        return defaultName;
      }
    },
    async exportTimesheetFile() {
      if (this.totalRows > 10000) {
        const message = "Большое количество записей. Отфильтруйте данные например по периоду";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.start_date)) {
        const message = "Выберите дату начала в фильтре";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.end_date)) {
        const message = "Выберите дату окончания в фильтре";
        this.showError(message);
        return;
      }

      const taskIds = this.selected.map(obj => obj.id);
      const locationIds = this.filters.locations.map(id => `&locations[]=${id}`);
      const usersIds = this.filters.users.map(id => `&executors[]=${id}`);

      const data = {
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        task_ids: taskIds
      }

      this.isBusyExport = true;
      const result = await this.postTaskTimesheetFile(data);

      if (!result || !result.data) {
        const title = "Ошибка создания файла";
        const message = "Не удалось выполнить создание файла. Попробуйте повторить позднее.";
        this.showGenerateFileError(title, message);
        this.isBusyExport = false;
        return;
      }

      this.isBusyExport = false;
      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },
    getUnit(unit_code) {
      const unit = this.getUnitByCode(unit_code);

      if (!unit) { return ""; }

      return unit.shortName || "";
    },
    onSelectOrg(org) {
      if (org === 0) {
        this.currentPayOrganization = null;
        return;
      }
      this.currentPayOrganization = org;
    },
    async getPaymentServicesWarning(taskIds) {
      for (const taskId of taskIds) {
        const result = await this.getTaskById(taskId);
        if (!result || result.error || !result.data || !Array.isArray(result.data.task_workers)) {
          return `Не удалось проверить услуги заявки №${taskId}. Оплата не отправлена. Обновите данные и повторите попытку.`;
        }
        const task = result.data;
        const worker = task.task_workers.find(item =>
          item.worker && Number(item.worker.employment_type) === 1 &&
          !item.paid && !['paid', 'in_progress'].includes(item.status_pay) &&
          (!task.timesheet_accounting || item.attendance_status === 'attended') &&
          !item.has_integration_details && !(item.task_worker_services || []).length
        );
        if (!worker) continue;

        const locationResult = await this.getLocationById(task.location_id);
        if (!locationResult || locationResult.error || !locationResult.data) {
          return `Заявка №${taskId}: у исполнителя ${worker.worker_id} не заполнены услуги. Добавьте их в разделе «Услуги исполнителя» и сохраните заявку. Если список пуст, проверьте вкладку «Услуги исполнителей» в карточке объекта.`;
        }
        const location = locationResult.data;
        if (!(location.location_work_services || []).length) {
          return `Заявка №${taskId}: у объекта «${location.name || task.location_name || task.location_id}» не настроены услуги исполнителей. Оплата через Баланс Про не отправлена. Добавьте услуги в карточке объекта на вкладке «Услуги исполнителей», затем выберите их для исполнителя в заявке и сохраните изменения.`;
        }
        return `Заявка №${taskId}: у исполнителя ${worker.worker_id} не заполнены услуги. Выберите их в разделе «Услуги исполнителя» и сохраните заявку перед оплатой.`;
      }
      return '';
    },
    async onPay(event) {
      if (event) event.preventDefault();
      if (this.isPaying) return;
      if (this.currentPayTaskData == null) {
        const message = "Не данных для оплаты";
        this.showError(message);
        return;
      }

      if (this.currentPayOrganization == null) {
        const message = "Не выбрана организация для оплаты";
        this.showError(message);
        return;
      }

      this.isPaying = true;
      this.paymentWarning = '';
      try {
        this.paymentWarning = await this.getPaymentServicesWarning(this.currentPayTaskData.task_ids);
        if (this.paymentWarning) return;

        const result = await this.postTaskUpdatePay(this.currentPayTaskData, this.currentPayOrganization);
        if (!result || result.error || !result.data) {
          this.showError(result && result.message || 'Не удалось получить результат операции. Проверьте состояние заявки перед повторной оплатой.');
          return;
        }
        this.paymentMessages = result.data.messages || [];
        this.paymentErrors = result.data.errors || [];

        this.$refs.modalSelectPayOrganization.hide();
        this.currentPayOrganization = null;
        this.currentPayTaskData = null;
        if (this.paymentMessages.length || this.paymentErrors.length) {
          this.$refs.modalPayResult.show();
        }
        await this.getItems();
      } finally {
        this.isPaying = false;
      }
    },
    /* Calendar */

    onCalendarPeriodChange() {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

      this.filters.start_date = this.formatDate(firstDay);
      this.filters.end_date = this.formatDate(lastDay);
      this.currentPage = 1;

      this.onSearch();
    },
    formatDayObject(date, isCurrentMonth) {
      const dateString = this.formatDate(date);
      const dayTasks = this.tasksByDateMap[dateString] || [];

      return {
        date: date,
        dayNumber: date.getDate(),
        isCurrentMonth: isCurrentMonth,
        dateString: dateString,
        visibleTasks: dayTasks.slice(0, 3),
        hiddenTasksCount: dayTasks.length > 3 ? dayTasks.length - 3 : 0
      };
    },
    handleCalendarTaskClick(task) {
      this.onRowDblClicked(task);
    },
    showTasksInTable(dateString) {
      this.filters.start_date = dateString;

      const nextDay = new Date(dateString);
      nextDay.setDate(nextDay.getDate() + 1);

      this.filters.end_date = this.formatDate(nextDay);

      this.currentPage = 1;
      this.activeTabIndex = 0;

      this.onSearch();
    },
  },
}
</script>

<style>
#tasks td {
  vertical-align: middle;
}

.export-button {
  margin-top: 4px;
}

/* скролл таблицы под b-tabs */

.tasks-page-section .page-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tasks-page-section .custom-tabs {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.tasks-page-section .custom-tabs .tab-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.tasks-page-section .custom-tabs .tab-pane {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Tasks/Tasks.vue